<template>
    <div data-testid="drag-dropfile" class="main">
        <div data-testid="drag-dropfile__dropzone" class="dropzone-container" @dragover="dragover" @dragleave="dragleave" @drop="drop">
            <input data-testid="drag-dropfile__input" type="file" :multiple="multiplefiles" name="file" id="fileInput" class="hidden-input" @change="onChange" ref="file"
            :accept="accept" />
            <label data-testid="drag-dropfile__label" for="fileInput" class="file-label d-flex align-center justify-center">
                <div data-testid="drag-dropfile__dragging" v-if="isDragging">Release to drop files here.</div>
                <div data-testid="drag-dropfile__selected-names" v-else-if="fileName && fileName.length > 0" class="file-label" >
                  {{ fileName.map(file => file.name).join(', ') }}
                </div>
                <div data-testid="drag-dropfile__empty" class="text-body-2 d-flex" v-else>
                    <p>Drag a file here (or)<span data-testid="drag-dropfile__upload-link" class="text-decoration-underline blue--text ma-0 pa-0 px-2">upload a file</span></p>
                </div>
            </label>
        </div>
        <div data-testid="drag-dropfile__selected-list" v-if="fileName && fileName.length > 0" class="selected-file-list">
          <div data-testid="drag-dropfile__selected-item" v-for="(file, index) in fileName" :key="file.name + index" class="selected-file">
            <span data-testid="drag-dropfile__selected-name" class="file-name">{{ file.name }}</span>
            <v-icon data-testid="drag-dropfile__remove-icon" class="remove-icon gray--text" @click.stop="removeFile(index)">mdi-close</v-icon>
          </div>
        </div>
    </div>
</template>

<script>
const BLOCKED_EXTENSIONS = new Set([
  'exe', 'msi', 'msp', 'msix', 'appx', 'appxbundle',
  'bat', 'cmd', 'com', 'scr', 'pif',
  'vbs', 'vbe', 'wsf', 'wsh',
  'ps1', 'psm1', 'psd1', 'ps1xml',
  'dll', 'sys', 'hta', 'jar',
  'reg', 'lnk', 'cpl', 'inf', 'msc',
  'app', 'deb', 'rpm', 'dmg',
  'application', 'gadget', 'diagcab',
]);

function getFileExtension(filename) {
  if (!filename || !filename.includes('.')) return '';
  return filename.split('.').pop().toLowerCase();
}

function isBlockedFile(file) {
  const ext = getFileExtension(file.name);
  return ext && BLOCKED_EXTENSIONS.has(ext);
}

export default {
  data() {
    return {
      isDragging: false,
      fileName: '',
      localFiles: []
    };
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    loadingpercent: {
      type: Number,
      default: 0,
    },
    files:{
      type: [Array, File],
      default: () => []
    },
    accept:{
      type:String,
      default: '.xlsx,.xls,.xlsm,.xlsb,.doc,.docx,.ppt,.pptx,.pdf,.png,.jpg,.jpeg,.svg,.mp4,.csv'
    },
    multiplefiles:{
      type:Boolean,
      default:true
    },
    resetTrigger: {  // New prop to trigger reset
      type: Boolean,
      default: false,
    },
  },
  mounted(){
    this.fileName="";
  },
  methods: {
    dragover(event) {
      event.preventDefault();
      this.isDragging = true;
    },
    dragleave(event) {
      event.preventDefault();
      this.isDragging = false;
    },
    drop(event) {
      event.preventDefault();
      this.isDragging = false;
      const files = event.dataTransfer.files;
      this.handleFiles(files);
    },
    onChange(event) {
  const files = event.target.files;
  this.handleFiles(files);
  // Reset file input so the same file can be added again after removal
  if (this.$refs.file) this.$refs.file.value = '';
    },
    handleFiles(files) {
      // Merge new files with existing localFiles, avoiding duplicates by name.
      // accept prop only controls the native file-picker filter; parent components
      // may apply their own allow-lists. Dangerous extensions are always blocked.
      let newFiles = Array.from(files);
      let mergedFiles = [...this.localFiles];
      newFiles.forEach(file => {
        if (!mergedFiles.some(f => f.name === file.name)) {
          mergedFiles.push(file);
        }
      });

      const blockedFiles = mergedFiles.filter(isBlockedFile);
      const safeFiles = mergedFiles.filter(file => !isBlockedFile(file));

      if (blockedFiles.length > 0) {
        const names = blockedFiles.map(file => file.name).join(', ');
        const message = `These file types are not allowed: ${names}`;
        console.error('[DragDropfile] Blocked file upload:', message);
        if (this.$store) {
          this.$store.commit('sbError', message);
        }
      }

      this.localFiles = safeFiles;
      this.fileName = safeFiles;
      this.$emit('update:files', this.localFiles);
    },
    removeFile(index) {
  this.localFiles.splice(index, 1);
  // Force new array reference for reactivity
  this.fileName = [...this.localFiles];
  this.$emit('update:files', this.localFiles);
    },
    resetFiles() {
      this.localFiles = [];
      this.fileName = [];
      if (this.$refs.file) this.$refs.file.value = '';
      this.$emit('update:files', []);
    },
  },
  watch: {
    files(newVal) {
      if (!newVal || (Array.isArray(newVal) && newVal.length === 0)) {
        this.localFiles = [];
        this.fileName = [];
        if (this.$refs.file) this.$refs.file.value = '';
      } else {
        this.localFiles = Array.isArray(newVal) ? newVal : [];
        this.fileName = this.localFiles;
      }
    },
    resetTrigger(newVal) {
      if (newVal) {
        this.resetFiles();
      }
    },
  },
};
</script>



<style lang="scss" scoped>
.main {
    // display: flex;
    flex-grow: 1;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 100%;
}

.dropzone-container {
    padding: 4rem;
    background: white;
    border: 1px dashed #ddd;
    width: 100%;
}

.hidden-input {
    opacity: 0;
    overflow: hidden;
    position: absolute;
    width: 1px;
    height: 1px;
}

.file-label {
    font-size: 15px !important;
    display: block;
    cursor: pointer;
}

.selected-file {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 0.5rem;
    padding: 0.5rem;
    background: #f9f9f9;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.file-name {
    flex-grow: 1;
    margin-right: 0.5rem;
}

.remove-icon {
    cursor: pointer;
    color: #f44336;
}
</style>